// File: src/pages/HomeRouter.jsx

import {
  useEffect,
  useMemo,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "@/context/AuthProvider";

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */

function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeRoles(user) {
  if (!user) return [];

  const roles = [
    ...(user.role
      ? [user.role]
      : []),

    ...(Array.isArray(user.roles)
      ? user.roles
      : []),
  ]
    .map(normalizeRole)
    .filter(Boolean);

  return Array.from(
    new Set(roles)
  );
}

function safeDecode(value) {
  try {
    return decodeURIComponent(
      String(value || "")
    );
  } catch {
    return String(
      value || ""
    );
  }
}

/**
 * Only allow application-internal redirect targets.
 *
 * Prevent:
 *   //evil.example.com
 *   https://evil.example.com
 *
 * Also avoid obvious authentication redirect loops.
 */
function isSafeInternalPath(path) {
  if (
    !path ||
    typeof path !== "string"
  ) {
    return false;
  }

  const value =
    path.trim();

  if (
    !value.startsWith("/")
  ) {
    return false;
  }

  if (
    value.startsWith("//")
  ) {
    return false;
  }

  /*
   * Avoid auth/error redirect loops.
   */
  const blockedPrefixes = [
    "/login",
    "/forbidden",
    "/unauthorized",
  ];

  if (
    blockedPrefixes.some(
      (prefix) =>
        value === prefix ||
        value.startsWith(
          `${prefix}/`
        ) ||
        value.startsWith(
          `${prefix}?`
        )
    )
  ) {
    return false;
  }

  return true;
}

function isPlatformAdminUser(user) {
  return (
    user?.is_platform_admin ===
    true
  );
}

function hasAnyRole(
  user,
  wantedRoles
) {
  const currentRoles =
    normalizeRoles(user);

  if (
    currentRoles.length === 0
  ) {
    return false;
  }

  const wanted =
    new Set(
      (
        Array.isArray(wantedRoles)
          ? wantedRoles
          : [wantedRoles]
      )
        .map(normalizeRole)
        .filter(Boolean)
    );

  if (
    wanted.size === 0
  ) {
    return false;
  }

  return currentRoles.some(
    (role) =>
      wanted.has(role)
  );
}

/*
 * Server-authoritative effective permission helper.
 *
 * Permissions originate from /auth/me and are supplied
 * through AuthProvider.
 */
function hasUserPermission(
  user,
  permission
) {
  if (
    !user ||
    !permission
  ) {
    return false;
  }

  const permissions =
    Array.isArray(
      user.permissions
    )
      ? user.permissions
      : [];

  const wanted =
    String(permission)
      .trim()
      .toLowerCase();

  return permissions.some(
    (value) =>
      String(value)
        .trim()
        .toLowerCase() ===
      wanted
  );
}

/* ──────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────── */

export default function HomeRouter() {
  const {
    user,
    ready,
    authenticated,
  } =
    useAuth();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const isEntry =
    useMemo(
      () =>
        location.pathname ===
          "/" ||
        location.pathname ===
          "/app",
      [
        location.pathname,
      ]
    );

  const returnTo =
    useMemo(() => {
      const params =
        new URLSearchParams(
          location.search ||
            ""
        );

      const raw =
        safeDecode(
          params.get(
            "returnTo"
          )
        );

      return isSafeInternalPath(
        raw
      )
        ? raw
        : "";
    }, [
      location.search,
    ]);

  useEffect(() => {
    /*
     * Wait for server-authoritative /auth/me bootstrap.
     *
     * `loading` is no longer appropriate here because that now represents
     * login/logout button activity rather than session bootstrap.
     */
    if (!ready) {
      return;
    }

    if (!isEntry) {
      return;
    }

    /* ──────────────────────────────────────────────────────────
     * Unauthenticated
     * ────────────────────────────────────────────────────────── */

    if (
      !authenticated ||
      !user
    ) {
      if (returnTo) {
        navigate(
          `/login?returnTo=${encodeURIComponent(
            returnTo
          )}`,
          {
            replace: true,
          }
        );
      } else {
        navigate(
          "/login",
          {
            replace: true,
          }
        );
      }

      return;
    }

    const isPlatformAdmin =
      isPlatformAdminUser(
        user
      );

    /* ──────────────────────────────────────────────────────────
     * Explicit returnTo
     *
     * ProtectedRoute remains the real frontend gate.
     *
     * HomeRouter only prevents the obvious case of a non-platform user
     * being sent directly into /admin.
     * ────────────────────────────────────────────────────────── */

    if (returnTo) {
      const wantsPlatformAdmin =
        returnTo ===
          "/admin" ||
        returnTo.startsWith(
          "/admin/"
        );

      if (
        wantsPlatformAdmin &&
        !isPlatformAdmin
      ) {
        navigate(
          "/dashboard",
          {
            replace: true,
          }
        );

        return;
      }

      navigate(
        returnTo,
        {
          replace: true,
        }
      );

      return;
    }

    /* ──────────────────────────────────────────────────────────
     * Default landing page
     * ────────────────────────────────────────────────────────── */

    if (isPlatformAdmin) {
      navigate(
        "/admin",
        {
          replace: true,
        }
      );

      return;
    }

    if (hasAnyRole(user, 'company_director')) {
      navigate('/company', { replace: true });
      return;
    }

    /*
     * Goods Receiving launch workspace.
     *
     * `viewer` is the launch-facing Goods Receiving role.
     *
     * We require BOTH:
     *
     * - the expected launch role
     * - the effective server permission
     *
     * The route itself will also be protected independently.
     */
    if (
      hasAnyRole(
        user,
        "viewer"
      ) &&
      hasUserPermission(
        user,
        "procurement.receiving.view"
      )
    ) {
      navigate(
        "/po-receiving-dashboard",
        {
          replace: true,
        }
      );

      return;
    }

    /*
     * Transitional role-based landing behavior.
     *
     * Roles come only from the server-validated AuthProvider user.
     *
     * Permissions/scopes/groups are deliberately NOT treated as roles.
     */
    if (
      hasAnyRole(
        user,
        [
          "admin",
          "finance",
          "manager",
          "developer",
        ]
      )
    ) {
      navigate(
        "/client-admin",
        {
          replace: true,
        }
      );

      return;
    }

    /*
     * All remaining authenticated users retain
     * the standard dashboard fallback.
     */
    navigate(
      "/dashboard",
      {
        replace: true,
      }
    );
  }, [
    user,
    ready,
    authenticated,
    navigate,
    isEntry,
    returnTo,
  ]);

  /*
   * Router-only component.
   *
   * While AuthProvider is bootstrapping, ProtectedRoute/LoginPage
   * can display their own appropriate UI.
   */
  return null;
}
